<template>
  <div class="app-container">
    <el-form class="demo-form-inline" label-position="top" size="small">
      <el-row :gutter="10">
        <el-col :span="6">
          <el-form-item :label="$t('CenterForSorting.machineName')">
            <el-select
              v-model="machineId"
              :placeholder="$t('CenterForSorting.pleaseInput')"
              size="small"
              style="width: 100%"
              @change="handleMachineChange"
            >
              <el-option
                v-for="item in machineList"
                :key="item.id"
                :label="item.machineName"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="6">
          <el-form-item :label="$t('CenterForSorting.selectRuleGroup')">
            <el-select
              v-model="ruleGroupId"
              :placeholder="$t('CenterForSorting.selectRuleGroup')"
              size="small"
              style="width: 100%"
              @change="fetchRules"
            >
              <el-option
                v-for="group in ruleGroups"
                :key="group.id"
                :label="group.groupName"
                :value="group.id"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="6">
          <!--    分拣条件      -->
          <el-form-item :label="$t('GlobalSetObj.SortingCondition')">
            <el-select
              v-model="conditionList"
              :placeholder="$t('GlobalSetObj.pleaseChoose')"
              clearable
              multiple
              size="small"
              style="width: 100%"
            >
              <el-option
                v-for="item in sortingConditionsList"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="6" style="margin-top: 27px">
          <el-button
            v-permit:remove="'btn:sorting:ruleList:search'"
            :disabled="!ruleGroupId"
            icon="el-icon-search"
            size="small"
            type="primary"
            @click="fetchRules"
          >{{ $t('CenterForSorting.search') }}
          </el-button>
        </el-col>
      </el-row>
    </el-form>

    <div style="display: flex; justify-content: space-between; align-items: center; margin: 10px 0">
      <div style="display: flex; align-items: center">
        <el-popover
          placement="bottom-start"
          trigger="hover"
          width="500"
          @hide="stopAnimation"
          @show="startAnimation"
        >
          <div class="rule-match-animation">
            <div class="animation-track">
              <!-- Step 1  -->
              <div
                :class="{ active: activeStep >= 1, rejected: activeStep >= 2 }"
                class="step-node"
              >
                <div class="node-icon">1</div>
                <div class="node-label">{{ $t('CenterForSorting.ruleMatchStep1') }}</div>
                <div v-if="activeStep >= 2" class="node-status">
                  <i class="el-icon-close" style="color: #f56c6c" />
                </div>
              </div>

              <div :class="{ active: activeStep >= 2 }" class="step-arrow">
                <i class="el-icon-right" />
              </div>

              <!-- Step 2 -->
              <div :class="{ active: activeStep >= 3, matched: activeStep >= 4 }" class="step-node">
                <div class="node-icon">2</div>
                <div class="node-label">{{ $t('CenterForSorting.ruleMatchStep2') }}</div>
                <div v-if="activeStep >= 4" class="node-status">
                  <i class="el-icon-check" style="color: #67c23a" />
                </div>
              </div>

              <div :class="{ active: activeStep >= 5 }" class="step-arrow">
                <i class="el-icon-right" />
              </div>

              <!-- Result -->
              <div :class="{ active: activeStep >= 5 }" class="step-result">
                <i class="el-icon-box" />
                <div class="result-label">{{ $t('CenterForSorting.returnChute') }}</div>
              </div>

              <!-- Skipped -->
              <div class="step-arrow skipped">
                <i class="el-icon-right" />
              </div>
              <div class="step-node skipped">
                <div class="node-icon">3</div>
                <div class="node-label">...</div>
              </div>
            </div>
            <div class="animation-desc">
              <i class="el-icon-info" style="color: #00a551; margin-right: 5px" />
              {{ $t('CenterForSorting.ruleMatchExplanation') }}
            </div>
          </div>
          <div slot="reference" class="rule-match-reference">
            <el-tooltip :content="$t('CenterForSorting.ruleMatchHint')" placement="top">
              <span class="rule-match-reference-text">{{
                $t('CenterForSorting.ruleMatchHint')
              }}</span>
            </el-tooltip>
            <i
              class="el-icon-video-play"
              style="color: #00a551; font-size: 18px; margin-right: 5px"
            />
          </div>
        </el-popover>
        <el-tooltip placement="top">
          <div slot="content" class="rule-explain-tooltip">
            <div>{{ $t('CenterForSorting.ruleExplainTitle') }}</div>
            <div>{{ $t('CenterForSorting.ruleExplainParentChildLabel') }}</div>
            <div>{{ $t('CenterForSorting.ruleExplainParentChildText') }}</div>
            <div>{{ $t('CenterForSorting.ruleExplainPriorityLabel') }}</div>
            <div>{{ $t('CenterForSorting.ruleExplainPriorityText') }}</div>
            <div>{{ $t('CenterForSorting.ruleExplainConfigLabel') }}</div>
            <div>{{ $t('CenterForSorting.ruleExplainConfigText') }}</div>
          </div>
          <i
            class="el-icon-question"
            style="color: #00a551; font-size: 18px; margin-left: 8px; cursor: pointer"
          />
        </el-tooltip>
      </div>

      <div>
        <el-tooltip
          v-permit:remove="'btn:sorting:ruleList:switch'"
          :content="$t('CenterForSorting.dragTip')"
          placement="top"
        >
          <i class="el-icon-question" style="margin-right: 5px; color: #909399; cursor: pointer" />
        </el-tooltip>
        <el-switch
          v-model="sortEnabled"
          v-permit:remove="'btn:sorting:ruleList:switch'"
          :active-text="$t('CenterForSorting.enableSort')"
          style="margin-right: 15px"
          @change="handleSortSwitch"
        />
        <el-button
          v-permit:remove="'btn:sorting:ruleList:export'"
          :disabled="!ruleGroupId"
          size="small"
          style="margin-right: 15px"
          type="primary"
          @click="handleExport"
        >{{ $t('CenterForSorting.exportRules') }}
        </el-button>
        <!--  新增规则      -->
        <el-tooltip
          v-permit:remove="'btn:sorting:ruleList:add'"
          :content="$t('CenterForSorting.fallbackRuleTip')"
          placement="top"
        >
          <div style="display: inline-block; margin-right: 10px">
            <el-button
              :disabled="!ruleGroupId"
              icon="el-icon-plus"
              size="small"
              type="primary"
              @click="handleAdd"
            >{{ $t('CenterForSorting.newRule') }}
            </el-button>
          </div>
        </el-tooltip>
        <!--  复制规则      -->
        <div style="display: inline-block; margin-right: 10px">
          <el-button
            v-permit:remove="'btn:sorting:ruleList:copy'"
            :disabled="!ruleGroupId"
            icon="el-icon-copy-document"
            size="small"
            type="success"
            @click="handleCopyOpen"
          >{{ $t('CenterForSorting.copyRule') }}
          </el-button>
          <!--  导入规则      -->
          <el-button
            v-permit:remove="'btn:sorting:ruleList:ImportRule'"
            :disabled="!ruleGroupId"
            size="small"
            type="success"
            @click="handleImportOpen"
          >{{ $t('CenterForSorting.importRules') }}
          </el-button>
        </div>
        <div style="display: inline-block">
          <el-dropdown
            v-permit:remove="'btn:sorting:ruleList:ruleGroupChangeLog'"
            trigger="hover"
            @command="handleGroupActionCommand"
          >
            <el-button
              :disabled="!ruleGroupId" icon="el-icon-more" size="small"
              type="warning"
            >{{ $t('CenterForSorting.otherActions') }}
            </el-button>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="groupChangeLog">
                <span slot="reference" class="dropdown-item-text">{{
                  $t('CenterForSorting.ruleGroupChangeLog')
                }}</span>
                <span>{{ $t('CenterForSorting.ruleGroupChangeLog') }}</span>
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </div>
    </div>
    <el-card class="table-card">
      <el-table
        v-loading="listLoading"
        :data="ruleList"
        border
        fit
        height="100%"
        highlight-current-row
        row-key="id"
        size="mini"
        style="width: 100%"
      >
        <el-table-column
          :label="$t('CenterForSorting.priority')"
          align="center"
          prop="priority"
          width="100"
        >
          <template slot-scope="{ row }">
            <!-- 假设拖拽手柄已足够，但显示数字也不错 -->
            {{ row.priority }}
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('CenterForSorting.ruleName')"
          align="left"
          min-width="260"
          prop="ruleName"
        >
          <template slot-scope="{ row }">
            <div class="rule-name-cell">
              <el-tag
                v-if="isTrueFlag(row.isAbnormalRule)"
                class="rule-name-tag"
                effect="plain"
                size="mini"
                type="danger"
              >
                {{ $t('CenterForSorting.abnormal') }}
              </el-tag>
              <el-tag
                v-if="isTrueFlag(row.isFallback)"
                class="rule-name-tag rule-name-tag-fallback"
                effect="plain"
                size="mini"
              >
                {{ $t('CenterForSorting.fallback') }}
              </el-tag>
              <el-tag
                v-if="isTrueFlag(row.isMachineFallbackRule)"
                class="rule-name-tag rule-name-tag-machine-fallback"
                effect="plain"
                size="mini"
              >
                {{ $t('CenterForSorting.machineFallbackTag') }}
              </el-tag>
              <span :title="row.ruleName" class="rule-name-text">{{ row.ruleName }}</span>
              <el-tooltip :content="$t('CenterForSorting.viewConditions')" placement="top">
                <i
                  class="el-icon-document rule-name-icon"
                  @click.stop="handleViewConditions(row)"
                />
              </el-tooltip>
            </div>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('CenterForSorting.targetChute')"
          align="center"
          min-width="160"
          prop="chuteNumber"
        >
          <template slot-scope="{ row }">
            <div v-if="row.chuteNumber">
              <el-tag
                v-for="(tag, index) in String(row.chuteNumber).split(',')"
                :key="index"
                size="mini"
                style="margin-right: 5px"
              >
                {{ tag }}
              </el-tag>
            </div>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('navbar.ruleLevel')"
          align="center"
          min-width="150"
          prop="ruleLevel"
        >
          <template slot-scope="{ row }">
            <div>
              <el-tag v-if="row.parentRuleId === '0'" size="mini" type="warning">
                {{ row.ruleLevel }}
              </el-tag>
              <el-tag v-else size="mini">
                {{ row.ruleLevel }}
              </el-tag>
            </div>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('GlobalSetObj.SortingCondition')"
          align="center"
          min-width="200"
          prop="conditionList"
        >
          <template slot-scope="{ row }">
            <el-tag
              v-for="(item, index) in row.conditionListName"
              :key="index"
              size="mini"
              style="margin-right: 5px"
            >
              {{ item }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('CenterForSorting.operation')"
          align="center"
          fixed="right"
          width="180"
        >
          <template slot-scope="{ row }">
            <el-tooltip
              v-permit:remove="'btn:sorting:ruleList:edit'"
              :content="$t('CenterForSorting.ruleGroupEnabledTip')"
              :disabled="isRuleGroupEditable"
              placement="top"
            >
              <span class="rule-action-item">
                <el-button
                  :disabled="!isRuleGroupEditable"
                  icon="el-icon-edit"
                  size="mini"
                  type="text"
                  @click="handleEdit(row)"
                >{{ $t('CenterForSorting.editRule') }}</el-button>
              </span>
            </el-tooltip>
            <el-dropdown
              trigger="hover"
              @command="(command) => handleRuleActionCommand(command, row)"
            >
              <span class="rule-more-action-trigger">
                <i class="el-icon-more" />
              </span>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item
                  v-permit:remove="'btn:sorting:ruleList:deleteRule'"
                  :disabled="!canDeleteRule"
                  command="deleteRule"
                >
                  <span slot="reference" class="dropdown-item-text">{{
                    $t('CenterForSorting.deleteRule')
                  }}</span>
                  <span>{{ $t('CenterForSorting.deleteRule') }}</span>
                </el-dropdown-item>
                <el-dropdown-item
                  v-permit:remove="'btn:sorting:ruleList:ruleChangeLog'"
                  command="ruleChangeLog"
                >
                  <span slot="reference" class="dropdown-item-text">{{
                    $t('CenterForSorting.ruleChangeLog')
                  }}</span>
                  <span>{{ $t('CenterForSorting.ruleChangeLog') }}</span>
                </el-dropdown-item>
                <el-dropdown-item
                  v-permit:remove="'btn:sorting:ruleList:editEnabled'"
                  :disabled="isRuleGroupEditable"
                  command="editEnabledRule"
                >
                  <span slot="reference" class="dropdown-item-text">{{
                    $t('CenterForSorting.editEnabledRule')
                  }}</span>
                  <span>{{ $t('CenterForSorting.editEnabledRule') }}</span>
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
    <el-dialog
      :close-on-click-modal="false"
      :visible.sync="dialogFormVisible"
      top="3vh"
      width="800px"
    >
      <div slot="title" class="rule-dialog-title">
        <span>{{ textMap[dialogStatus] }}</span>
        <el-button
          v-if="dialogStatus === 'updateEnabled'"
          v-permit:remove="'btn:sorting:ruleList:forceChange'"
          :type="forceChangeEnabled ? 'danger' : 'default'"
          icon="el-icon-unlock"
          size="mini"
          @click="toggleForceChange"
        >
          {{ $t('CenterForSorting.forceChange') }}
        </el-button>
      </div>
      <el-form
        ref="dataForm"
        :model="temp"
        :rules="rules"
        label-position="right"
        label-width="190px"
      >
        <el-form-item :label="$t('CenterForSorting.ruleName')" prop="ruleName">
          <el-input v-model="temp.ruleName" :placeholder="$t('CenterForSorting.pleaseInput')" />
        </el-form-item>
        <el-form-item :label="$t('CenterForSorting.targetChute')" prop="chuteNumber">
          <el-select
            v-model="tempForm.chuteNumber"
            :placeholder="$t('CenterForSorting.pleaseInput')"
            filterable
            multiple
            style="width: 100%"
            @change="handleChuteChange"
          >
            <el-option
              v-for="item in targetChuteOptions"
              :key="item.value"
              :disabled="isTargetChuteOptionDisabled(item)"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <!--  父级规则      -->
        <el-form-item :label="$t('navbar.parentRule')" prop="parentRuleIds">
          <el-select
            v-model="temp.parentRuleIds"
            :placeholder="$t('CenterForSorting.pleaseChoose')"
            clearable
            filterable
            multiple
            style="width: 100%"
            @change="handleRuleIds"
          >
            <el-option
              v-for="item in parentRuleList"
              :key="item.id"
              :label="item.ruleName"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('CenterForSorting.priority')" prop="priority">
          <el-input-number v-model="temp.priority" :min="1" />
        </el-form-item>
        <el-form-item :label="$t('CenterForSorting.matchLogic')" prop="matchLogic">
          <el-radio-group v-model="temp.matchLogic">
            <el-radio label="OR">{{ $t('CenterForSorting.OR') }}</el-radio>
            <el-radio label="AND">{{ $t('CenterForSorting.AND') }}</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item :label="$t('CenterForSorting.isFallback')" prop="isFallback">
          <div class="fallback-check-group">
            <span class="fallback-check-item">
              <el-checkbox
                v-model="temp.isFallback"
                @change="(val) => handleFallbackChange(val, 'system')"
              >
                {{ $t('CenterForSorting.systemFallback') }}
              </el-checkbox>
              <el-tooltip :content="$t('CenterForSorting.systemFallbackTooltip')" placement="top">
                <i class="el-icon-question fallback-check-tip" />
              </el-tooltip>
            </span>
            <span class="fallback-check-item">
              <el-checkbox
                v-model="temp.isMachineFallbackRule"
                @change="(val) => handleFallbackChange(val, 'machine')"
              >
                {{ $t('CenterForSorting.machineFallback') }}
              </el-checkbox>
              <el-tooltip :content="$t('CenterForSorting.machineFallbackTooltip')" placement="top">
                <i class="el-icon-question fallback-check-tip" />
              </el-tooltip>
            </span>
          </div>
        </el-form-item>
        <el-form-item :label="$t('CenterForSorting.isAbnormalRule')" prop="isAbnormalRule">
          <el-radio-group v-model="temp.isAbnormalRule">
            <el-radio :label="0">{{ $t('CenterForSorting.no') }}</el-radio>
            <el-radio :label="1">{{ $t('CenterForSorting.yes') }}</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item :label="$t('CenterForSorting.ruleDesc')" prop="description">
          <el-input
            v-model="temp.description"
            :placeholder="$t('CenterForSorting.pleaseInput')"
            type="textarea"
          />
        </el-form-item>

        <el-divider v-show="!isAnyFallbackRule(temp)" content-position="left">{{ $t('CenterForSorting.condition') }}
        </el-divider>

        <el-form-item v-show="!isAnyFallbackRule(temp)" prop="conditions">
          <rule-condition-form
            v-model="temp.conditions"
            :condition-configs="conditionConfigs"
            :disabled="isAnyFallbackRule(temp)"
            :is-update-enabled="isUpdateEnabledLocked"
            prop-prefix="conditions"
          />
        </el-form-item>
      </el-form>
      <div class="network-point-refresh-tip">
        <i class="el-icon-warning network-point-refresh-icon" />
        <span>{{ $t('CenterForSorting.networkPointRefreshTipBefore') }}</span>
        <span
          :class="{ 'is-loading': conditionConfigsLoading }"
          class="network-point-refresh-link"
          @click="refreshConditionConfigs"
        >{{ $t('CenterForSorting.networkPointRefreshLink') }}</span>
        <span>{{ $t('CenterForSorting.networkPointRefreshTipAfter') }}</span>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogFormVisible = false">{{ $t('CenterForSorting.cancel') }}
        </el-button>
        <el-button
          :loading="dialogBtnLoading"
          type="primary"
          @click="dialogStatus === 'create' ? createData() : updateData()"
        >
          {{ $t('CenterForSorting.confirm') }}
        </el-button>
      </div>
    </el-dialog>

    <!-- 查看条件对话框 -->
    <rule-condition-detail
      :condition-configs="conditionConfigs"
      :rule-id="currentRuleId"
      :visible.sync="viewConditionsVisible"
    />

    <el-dialog
      :title="changeLogDialogTitle"
      :visible.sync="changeLogDialogVisible"
      append-to-body
      width="760px"
      @close="handleCloseRuleChangeLog"
    >
      <div
        ref="ruleChangeLogScrollWrap"
        class="rule-change-log-wrap"
        @scroll="handleRuleChangeLogScroll"
      >
        <div v-for="item in ruleChangeLogList" :key="item.id" class="rule-change-log-item">
          <div class="rule-change-log-content">
            <div class="rule-change-log-main">
              {{
                $t('CenterForSorting.ruleChangeLogSentence', {
                  user: item.operationUserName || '--',
                  time: item.createTime || '--',
                  action: getOperationTypeLabel(item.operationType)
                })
              }}
            </div>
            <div class="rule-change-log-sub">
              {{ item.ruleGroupName || '--' }}
              <template v-if="item.ruleName"> / {{ item.ruleName }}</template>
            </div>
          </div>
          <el-tooltip :content="$t('CenterForSorting.viewChangeDetail')" placement="top">
            <i
              class="el-icon-data-analysis rule-change-detail-icon"
              @click="openChangeCompare(item)"
            />
          </el-tooltip>
        </div>
        <div v-if="ruleChangeLogLoading" class="rule-change-log-tip">
          <i class="el-icon-loading" />
          <span>{{ $t('CenterForSorting.loading') }}</span>
        </div>
        <div v-else-if="!ruleChangeLogList.length" class="rule-change-log-tip">
          {{ $t('CenterForSorting.noChangeLogData') }}
        </div>
        <div v-else-if="ruleChangeLogFinished" class="rule-change-log-tip">
          {{ $t('CenterForSorting.noMoreData') }}
        </div>
      </div>
    </el-dialog>

    <rule-change-compare-dialog
      :after-data="currentAfterData"
      :before-data="currentBeforeData"
      :condition-configs="conditionConfigs"
      :visible.sync="changeCompareVisible"
    />

    <!-- 复制规则 对话框 -->
    <el-dialog
      :close-on-click-modal="false"
      :title="$t('CenterForSorting.copyRule')"
      :visible.sync="copyDialogVisible"
      width="1000px"
      @close="closeCopyDialog"
    >
      <el-form :inline="true" class="copy-dialog-form" size="small">
        <el-form-item :label="$t('CenterForSorting.sourceMachine')">
          <el-select
            v-model="copySourceMachineId"
            :placeholder="$t('CenterForSorting.pleaseChoose')"
            @change="handleCopyMachineChange"
          >
            <el-option
              v-for="item in machineList"
              :key="item.id"
              :label="item.machineName"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('CenterForSorting.sourceRuleGroup')">
          <el-select
            v-model="copySourceRuleGroupId"
            :placeholder="$t('CenterForSorting.pleaseChoose')"
            @change="fetchCopySourceRules"
          >
            <el-option
              v-for="group in copySourceRuleGroups"
              :key="group.id"
              :label="group.groupName"
              :value="group.id"
            />
          </el-select>
        </el-form-item>
      </el-form>

      <el-table
        ref="copyRulesTable"
        v-loading="copyListLoading"
        :data="copyRuleList"
        border
        fit
        highlight-current-row
        max-height="400"
        style="width: 100%; margin-top: 10px"
        @selection-change="handleCopySelectionChange"
      >
        <el-table-column align="center" type="selection" width="55" />
        <el-table-column :label="$t('CenterForSorting.ruleName')" align="center" min-width="150">
          <template slot-scope="{ row }">
            <div style="display: flex; align-items: center; justify-content: center">
              <span
                :title="row.ruleName"
                style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis"
              >
                {{ row.ruleName }}
              </span>
              <i
                :title="$t('CenterForSorting.viewConditions')"
                class="el-icon-document"
                style="
                  flex-shrink: 0;
                  margin-left: 5px;
                  cursor: pointer;
                  color: #00a551;
                  font-size: 16px;
                "
                @click.stop="handleViewConditions(row)"
              />
            </div>
          </template>
        </el-table-column>
        <el-table-column :label="$t('CenterForSorting.matchLogic')" align="center" width="150">
          <template slot-scope="{ row }">
            {{ row.matchLogic === 'AND' ? $t('CenterForSorting.AND') : $t('CenterForSorting.OR') }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('CenterForSorting.targetChute')" align="center" width="250">
          <template slot-scope="{ row }">
            <el-select
              v-model="row.newChuteNumberArr"
              :placeholder="$t('CenterForSorting.pleaseChoose')"
              filterable
              multiple
              size="mini"
              style="width: 100%"
            >
              <el-option
                v-for="item in targetChuteOptions"
                :key="item.value"
                :disabled="item.disabled"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </template>
        </el-table-column>
      </el-table>

      <div slot="footer" class="dialog-footer">
        <el-button @click="closeCopyDialog">{{ $t('CenterForSorting.cancel') }}</el-button>
        <el-button :loading="copyBtnLoading" type="primary" @click="confirmCopyRules">
          {{ $t('CenterForSorting.confirm') }}
        </el-button>
      </div>
    </el-dialog>
    <bulkImport ref="bulkImportRef" @import-success="handleImportSuccess" />
  </div>
</template>

<script>
import Sortable from 'sortablejs';
import { getRuleGroupPage } from '@/api/sorting/ruleGroup';
import { checkOperatePermission, getAllMachineList } from '@/api/sorting/machine';
import {
  addRule,
  checkChuteDuplicate,
  clearConditionConfigCache,
  deleteRule,
  exportRules,
  getConditionConfigList,
  getListRules,
  getNextPriority,
  getRuleChangeLogPage,
  listChutes,
  listConditions,
  listParentRules,
  updateRule,
  updateRulePriority
} from '@/api/sorting/rule';
import RuleConditionForm from './components/RuleConditionForm';
import RuleConditionDetail from './components/RuleConditionDetail';
import RuleChangeCompareDialog from './components/RuleChangeCompareDialog';
import bulkImport from './components/bulkImport';
import { conditionTypes, waybillStatusListRule } from '@/utils/enumData';
import { goResourceTaskList } from '@/utils/goExportList';

export default {
  name: 'RuleList',
  components: { RuleConditionForm, RuleConditionDetail, RuleChangeCompareDialog, bulkImport },
  data() {
    return {
      machineId: null,
      machineList: [],
      ruleGroupId: null,
      ruleGroups: [],
      ruleList: [],
      listLoading: false,
      conditionConfigs: [],
      conditionConfigsLoading: false,
      dialogFormVisible: false,
      dialogStatus: '',
      forceChangeEnabled: false,
      targetChuteOptions: [],
      textMap: {
        update: this.$t('CenterForSorting.editRule'),
        create: this.$t('CenterForSorting.newRule'),
        updateEnabled: this.$t('CenterForSorting.editEnabledRule')
      },
      temp: {
        id: undefined,
        ruleGroupId: undefined,
        ruleName: '',
        chuteNumber: '', // 为了兼容API，这里可能是字符串。如果是多选，表单中会是数组但需要转换。
        priority: 1,
        matchLogic: 'OR',
        isFallback: false,
        isMachineFallbackRule: false,
        isAbnormalRule: 0,
        oldIsFallback: null,
        oldIsMachineFallbackRule: null,
        description: '',
        conditions: [],
        parentRuleIds: []
      },
      // 表单使用的本地临时变量（用于处理数组类型的chuteNumber）
      tempForm: {
        chuteNumber: []
      },
      dialogBtnLoading: false,
      rules: {
        ruleName: [
          { required: true, message: this.$t('CenterForSorting.pleaseInput'), trigger: 'blur' }
        ],
        chuteNumber: [
          { required: true, message: this.$t('CenterForSorting.pleaseInput'), trigger: 'change' }
        ],
        priority: [
          { required: true, message: this.$t('CenterForSorting.pleaseInput'), trigger: 'blur' }
        ],
        conditions: [
          {
            validator: (rule, value, callback) => {
              if (this.forceChangeEnabled && this.dialogStatus === 'updateEnabled') {
                callback();
                return;
              }
              if (!this.isAnyFallbackRule(this.temp) && (!value || value.length === 0)) {
                callback(new Error(this.$t('CenterForSorting.conditionNotEmpty')));
              } else {
                callback();
              }
            },
            trigger: 'change'
          }
        ]
      },
      viewConditionsVisible: false,
      currentRuleId: null,
      sortEnabled: false,
      sortable: null,
      activeStep: 0,
      animationTimer: null,
      waybillStatusList: [], // 不再直接使用导入的静态列表

      // 复制规则相关变量
      copyDialogVisible: false,
      copySourceMachineId: null,
      copySourceRuleGroupId: null,
      copySourceRuleGroups: [],
      copyRuleList: [],
      copySelectedRules: [],
      copyListLoading: false,
      copyBtnLoading: false,
      changeLogDialogVisible: false,
      changeLogDialogType: 'group',
      changeLogRuleId: null,
      ruleChangeLogLoading: false,
      ruleChangeLogList: [],
      ruleChangeLogPageNumber: 1,
      ruleChangeLogPageSize: 10,
      ruleChangeLogTotal: 0,
      ruleChangeLogFinished: false,
      changeCompareVisible: false,
      currentBeforeData: null,
      currentAfterData: null,
      sortingConditionsList: conditionTypes,
      conditionList: [],
      parentRuleList: []
    };
  },
  computed: {
    currentRuleGroup() {
      if (!this.ruleGroupId) return null;
      return this.ruleGroups.find((g) => g.id === this.ruleGroupId);
    },
    isRuleGroupEditable() {
      return this.currentRuleGroup && this.currentRuleGroup.status === 0;
    },
    // 规则组启用时默认禁止删除规则；管理员角色不置灰（后端仍按管理员角色+Nacos 账号清单兜底校验）
    canDeleteRule() {
      return this.isRuleGroupEditable || this.$store.state.user.adminRole;
    },
    isUpdateEnabledLocked() {
      return this.dialogStatus === 'updateEnabled' && !this.forceChangeEnabled;
    },
    changeLogDialogTitle() {
      return this.changeLogDialogType === 'group'
        ? this.$t('CenterForSorting.ruleGroupChangeLog')
        : this.$t('CenterForSorting.ruleChangeLog');
    }
  },
  watch: {
    '$i18n.locale'() {
      // 语言切换时重新加载配置，以更新多语言标签
      this.fetchConditionConfigs();
    },
    '$route.query.ruleGroupId'(newRuleGroupId, oldRuleGroupId) {
      if (String(newRuleGroupId || '') === String(oldRuleGroupId || '')) return;
      this.syncRouteRuleGroup({ forceReload: true });
    }
  },
  async created() {
    await this.fetchMachines();
    await this.syncRouteRuleGroup({ forceReload: true });
    await this.fetchConditionConfigs();
  },
  activated() {
    this.syncRouteRuleGroup({ forceReload: true });
  },
  methods: {
    // 新增/复制/导入规则等操作前，统一校验当前设备是否具备操作权限。
    // 校验通过（data 为 true）返回 true，否则提示并返回 false，调用方据此决定是否继续打开弹框。
    async checkOperatePermissionBeforeAction() {
      if (!this.machineId) {
        this.$message.warning(this.$t('CenterForSorting.machineName'));
        return false;
      }
      try {
        const res = await checkOperatePermission({ machineId: this.machineId });
        if (!res.data) {
          this.$message.warning(this.$t('CenterForSorting.noOperatePermission'));
          return false;
        }
        return true;
      } catch (error) {
        return false;
      }
    },
    // 如果选择大于2提示
    handleRuleIds(val) {
      if (val.length > 2) {
        this.$message.error(this.$t('CenterForSorting.maxTwoParentRules'));
        // 保留前两个选中的值
        this.temp.parentRuleIds = val.slice(0, 2);
        return;
      }
    },
    // 获取父级规则
    async getListRules() {
      const res = await listParentRules(this.ruleGroupId);
      this.parentRuleList = res.data;
    },
    parseRouteRuleGroupId(ruleGroupId) {
      const parsedRuleGroupId = Number(ruleGroupId);
      return Number.isNaN(parsedRuleGroupId) || parsedRuleGroupId <= 0 ? null : parsedRuleGroupId;
    },
    async syncRouteRuleGroup({ forceReload = false } = {}) {
      const routeRuleGroupId = this.parseRouteRuleGroupId(this.$route.query.ruleGroupId);
      const ruleGroupChanged = routeRuleGroupId !== this.ruleGroupId;

      if (routeRuleGroupId) {
        this.ruleGroupId = routeRuleGroupId;
        if (forceReload || ruleGroupChanged || !this.ruleGroups.length || !this.machineId) {
          await this.fetchRuleGroups(true);
        }
        if (this.ruleGroupId && (ruleGroupChanged || forceReload || !this.ruleList.length)) {
          this.fetchRules();
        }
        return;
      }

      if (!this.machineId && this.machineList.length > 0) {
        this.machineId = this.machineList[0].id;
      }
      if (forceReload || this.machineId || !this.ruleGroups.length) {
        await this.fetchRuleGroups();
      }
      if (this.ruleGroupId && (forceReload || !this.ruleList.length)) {
        this.fetchRules();
      } else if (!this.ruleGroupId) {
        this.ruleList = [];
      }
    },
    isTrueFlag(value) {
      return value === true || value === 1 || value === '1';
    },
    // 统一判断“系统兜底/分拣机兜底”任一开关是否开启，供表单展示与校验复用。
    isAnyFallbackRule(rule) {
      if (!rule) return false;
      return this.isTrueFlag(rule.isFallback) || this.isTrueFlag(rule.isMachineFallbackRule);
    },
    // 将后端可能返回的 0/1/布尔值统一归一化为布尔，避免 checkbox 回显异常。
    normalizeToBoolean(value) {
      return this.isTrueFlag(value);
    },
    // 组装“分拣机兜底”专用条件。优先读取配置中心的默认操作符和值，兜底采用 EQ + 1。
    buildMachineFallbackCondition() {
      const machineFallbackConfig =
        this.conditionConfigs.find((item) => item.conditionType === 'MACHINE_FALLBACK') || {};
      const operators = Array.isArray(machineFallbackConfig.operators)
        ? machineFallbackConfig.operators
        : [];
      const allowedValues = Array.isArray(machineFallbackConfig.allowedValues)
        ? machineFallbackConfig.allowedValues
        : [];
      const fallbackConditionValue =
        allowedValues.length > 0 && allowedValues[0] && allowedValues[0].value !== undefined
          ? String(allowedValues[0].value)
          : '1';
      console.log(this.conditionConfigs, 'this.conditionConfigs01');
      return {
        conditionType: 'MACHINE_FALLBACK',
        operator: operators.length > 0 ? operators[0] : 'EQ',
        conditionValue: fallbackConditionValue
      };
    },
    // 提交前统一收敛条件数据，兜底规则不允许携带普通业务条件。
    buildSubmitConditions() {
      if (this.isTrueFlag(this.temp.isFallback)) {
        return [];
      }
      if (this.isTrueFlag(this.temp.isMachineFallbackRule)) {
        return [this.buildMachineFallbackCondition()];
      }
      const conditions = Array.isArray(this.temp.conditions) ? this.temp.conditions : [];
      // 包裹重量条件值为输入框字符串，提交前统一转为数值，保持与后端契约一致
      return conditions.map((item) => {
        if (item && item.conditionType === 'PACKAGE_WEIGHT') {
          const { conditionValue } = item;
          if (
            conditionValue !== '' &&
            conditionValue !== null &&
            conditionValue !== undefined &&
            !Number.isNaN(Number(conditionValue))
          ) {
            return { ...item, conditionValue: Number(conditionValue) };
          }
        }
        return item;
      });
    },
    handleGroupActionCommand(command) {
      if (command === 'groupChangeLog') {
        this.openRuleGroupChangeLog();
      }
    },
    handleRuleActionCommand(command, row) {
      if (command === 'deleteRule') {
        this.handleDelete(row);
        return;
      }
      if (command === 'ruleChangeLog') {
        this.openRuleChangeLog(row);
        return;
      }
      if (command === 'editEnabledRule') {
        this.handleEditEnabled(row);
      }
    },
    async fetchMachines() {
      const res = await getAllMachineList();
      this.machineList = res.data || [];
    },
    handleMachineChange() {
      this.ruleGroupId = null;
      this.ruleList = [];
      this.fetchRuleGroups();
    },
    async fetchRuleGroups(fetchAll = false) {
      const params = { pageNumber: 1, pageSize: 1000 };
      // 如果不是获取全部，则按machineId筛选
      if (this.machineId && !fetchAll) {
        params.params = { machineId: this.machineId };
      }

      const res = await getRuleGroupPage(params);
      const fetchedGroups = res.data.records || [];

      // 如果是获取全部（例如初始加载带有ruleGroupId），使用此列表
      // 如果是按特定设备获取，更新列表
      this.ruleGroups = fetchedGroups;

      if (this.ruleGroupId) {
        // 尝试优先匹配数字ID
        let group = this.ruleGroups.find((g) => g.id === this.ruleGroupId);
        // 如果未找到，尝试宽松匹配
        if (!group) {
          group = this.ruleGroups.find((g) => String(g.id) === String(this.ruleGroupId));
          if (group) this.ruleGroupId = group.id; // 修正类型
        }

        if (group) {
          // 找到规则组
          this.machineId = group.machineId;
          // 修正列表以便UI显示正确
          if (fetchAll) {
            this.ruleGroups = this.ruleGroups.filter((g) => g.machineId === this.machineId);
          }
        } else {
          // 在获取的列表中未找到规则组
          // 如果获取了全部仍未找到，则是无效ID
          // 如果是过滤后的列表未找到，说明它不属于该设备
          this.ruleGroupId = null;
        }
      } else {
        // 如果未选择规则组，且已设置machineId，只显示该设备的规则组
        // 如果fetchAll为true（未选规则组时不应常发生），可能需要过滤？
        // 但上面的逻辑已处理machineId参数。
        // 仅确保如果清除了ruleGroupId，且是fetchAll，仍遵守machineId过滤
        if (fetchAll && this.machineId) {
          this.ruleGroups = this.ruleGroups.filter((g) => g.machineId === this.machineId);
        }
      }

      // 如果未选择规则组（或已清除），且未选择设备但有设备列表，默认选择第一个设备
      if (!this.ruleGroupId && this.machineList.length > 0 && !this.machineId) {
        this.machineId = this.machineList[0].id;
        if (!fetchAll) {
          await this.fetchRuleGroups(false);
        }
      }
    },
    async fetchConditionConfigs() {
      const res = await getConditionConfigList();
      this.conditionConfigs = res.data || [];
      // 兜底补充「包裹重量(kg)」条件：后端条件配置未下发时，前端保证该条件可选
      this.ensurePackageWeightConditionConfig();
      // 获取最新的多语言状态列表
      const statusList = waybillStatusListRule;
      this.conditionConfigs.forEach((item) => {
        if (item.conditionType === 'WAYBILL_STATUS') {
          item.allowedValues = statusList.map((status) => ({
            label: status.label,
            value: status.code
          }));
        }
      });
      console.log(this.conditionConfigs, 'this.conditionConfigs01');
    },
    // 确保「包裹重量(kg)」条件配置可用：运算符为 ≤(LTE)/＞(GT)，值为 3 位小数。
    // 后端未下发该条件、或下发但 operators 缺失/为空时补齐，避免运算符下拉为空导致提交缺少 operator
    ensurePackageWeightConditionConfig() {
      const defaultOperators = ['LTE', 'GT'];
      const target = this.conditionConfigs.find(
        (item) => item && item.conditionType === 'PACKAGE_WEIGHT'
      );
      if (!target) {
        this.conditionConfigs.push({
          conditionType: 'PACKAGE_WEIGHT',
          operators: defaultOperators,
          valueType: 'Weight',
          allowedValues: []
        });
        return;
      }
      if (!Array.isArray(target.operators) || target.operators.length === 0) {
        this.$set(target, 'operators', defaultOperators);
      }
    },
    // 手动刷新条件配置（获取最新网点等下拉数据），用于弹框底部“点击刷新”
    // 先清空后端条件配置缓存，成功后再拉取最新数据
    async refreshConditionConfigs() {
      if (this.conditionConfigsLoading) return;
      this.conditionConfigsLoading = true;
      try {
        await clearConditionConfigCache();
        await this.fetchConditionConfigs();
        this.$message.success(this.$t('CenterForSorting.networkPointRefreshSuccess'));
      } catch (error) {
        this.$message.error(this.$t('CenterForSorting.networkPointRefreshFailed'));
      } finally {
        this.conditionConfigsLoading = false;
      }
    },
    fetchRules() {
      if (!this.ruleGroupId) return;
      this.listLoading = true;
      getListRules({ ruleGroupId: this.ruleGroupId, conditionList: this.conditionList })
        .then((response) => {
          this.ruleList = response.data || [];
          this.listLoading = false;
          this.$nextTick(() => {
            this.setSort();
          });
        })
        .catch(() => {
          this.listLoading = false;
        });
    },
    handleSortSwitch(val) {
      if (this.sortable) {
        this.sortable.option('disabled', !val);
      }
    },
    // 导出
    handleExport() {
      if (!this.ruleGroupId) {
        this.$message.warning(this.$t('CenterForSorting.selectRuleGroup'));
        return;
      }
      exportRules({ ruleGroupId: this.ruleGroupId, conditionList: this.conditionList }).then(
        (res) => {
          if (res.code === 200) {
            goResourceTaskList();
          }
        }
      );
    },
    setSort() {
      const el = this.$el.querySelectorAll('.el-table__body-wrapper > table > tbody')[0];
      if (!el) return;
      if (this.sortable) {
        this.sortable.destroy();
        this.sortable = null;
      }
      this.sortable = Sortable.create(el, {
        ghostClass: 'sortable-ghost',
        disabled: !this.sortEnabled,
        setData: function(dataTransfer) {
          dataTransfer.setData('Text', '');
        },
        onEnd: (evt) => {
          const targetRow = this.ruleList.splice(evt.oldIndex, 1)[0];
          this.ruleList.splice(evt.newIndex, 0, targetRow);

          // 重新计算所有规则的优先级
          const updateData = this.ruleList.map((item, index) => {
            const newPriority = index + 1;
            item.priority = newPriority; // 更新视图
            return {
              id: item.id,
              priority: newPriority
            };
          });

          // 调用批量更新接口
          updateRulePriority(updateData).then(() => {
            this.$message.success(this.$t('CenterForSorting.statusUpdateSuccess'));
            // this.sortEnabled = false; // 关闭拖动排序开关
            // this.handleSortSwitch(false); // 禁用 Sortable
            this.fetchRules(); // 刷新以获取一致状态
          });
        }
      });
    },
    resetTemp() {
      this.forceChangeEnabled = false;
      this.temp = {
        id: undefined,
        ruleGroupId: this.ruleGroupId,
        ruleName: '',
        chuteNumber: '',
        priority: 1,
        matchLogic: 'OR',
        isFallback: false,
        isMachineFallbackRule: false,
        isAbnormalRule: 0,
        oldIsFallback: null,
        oldIsMachineFallbackRule: null,
        description: '',
        conditions: [],
        parentRuleIds: []
      };
      this.tempForm.chuteNumber = [];
    },
    async generateChuteOptions(boundChutes = [], isUpdateEnabled = false) {
      if (!this.machineId || !this.ruleGroupId) return;
      const machine = this.machineList.find((m) => m.id === this.machineId);
      if (machine && machine.machineCode) {
        try {
          const res = await listChutes({
            machineCode: machine.machineCode,
            ruleGroupId: this.ruleGroupId
          });
          const chutes = res.data || [];
          chutes.sort((a, b) => {
            if (a.disabled === b.disabled) {
              return String(a.chuteNumber).localeCompare(String(b.chuteNumber), undefined, {
                numeric: true
              });
            }
            return a.disabled ? 1 : -1;
          });
          this.targetChuteOptions = chutes.map((item) => {
            const isBound = boundChutes.includes(String(item.chuteNumber));
            let disabled = item.disabled;
            if (isBound) {
              disabled = isUpdateEnabled;
            }
            return {
              value: item.chuteNumber,
              label: item.chuteNumber,
              disabled
            };
          });
        } catch (error) {
          console.error('Failed to generate chute options:', error);
        }
      }
    },
    async handleAdd() {
      if (!(await this.checkOperatePermissionBeforeAction())) return;
      this.resetTemp();
      await Promise.all([this.generateChuteOptions(), this.getListRules()]);
      // 获取下一个优先级
      const res = await getNextPriority({ ruleGroupId: this.ruleGroupId });
      this.temp.priority = res.data || 1;

      this.dialogStatus = 'create';
      this.dialogFormVisible = true;
      this.$nextTick(() => {
        this.$refs['dataForm'].clearValidate();
      });
    },
    // 中文注释：系统兜底与分拣机兜底互斥控制。允许都不选，但不允许同时选中。
    handleFallbackChange(val, type) {
      if (type === 'system' && val) {
        this.temp.isMachineFallbackRule = false;
      }
      if (type === 'machine' && val) {
        this.temp.isFallback = false;
      }

      // 中文注释：任一兜底状态变化后都清空条件，避免切换类型后遗留历史条件误提交。
      const fallbackStatusChanged =
        this.temp.oldIsFallback !== this.temp.isFallback ||
        this.temp.oldIsMachineFallbackRule !== this.temp.isMachineFallbackRule;
      if (this.isAnyFallbackRule(this.temp) || fallbackStatusChanged) {
        this.temp.conditions = [];
        this.$nextTick(() => {
          this.$refs['dataForm'].clearValidate('conditions');
        });
      }

      this.temp.oldIsFallback = this.temp.isFallback;
      this.temp.oldIsMachineFallbackRule = this.temp.isMachineFallbackRule;
    },
    toggleForceChange() {
      this.forceChangeEnabled = !this.forceChangeEnabled;
      this.$nextTick(() => {
        this.$refs['dataForm'].clearValidate();
      });
    },
    isTargetChuteOptionDisabled(item) {
      return item.disabled;
    },
    handleChuteChange(val) {
      if (this.isUpdateEnabledLocked) {
        const boundChutes = this.temp.chuteNumber ? this.temp.chuteNumber.split(',') : [];
        const missing = boundChutes.filter((chute) => !val.includes(chute));
        if (missing.length > 0) {
          // 还原丢失的已绑定项
          this.tempForm.chuteNumber = [...new Set([...val, ...missing])];
          this.temp.chuteNumber = this.tempForm.chuteNumber.join(',');
          return;
        }
      }
      this.temp.chuteNumber = val.join(',');
      this.checkChute();
    },
    checkChute() {
      if (this.forceChangeEnabled && this.dialogStatus === 'updateEnabled') return;
      if (!this.tempForm.chuteNumber.length || !this.ruleGroupId) return;
      checkChuteDuplicate({
        ruleGroupId: this.ruleGroupId,
        chuteNumber: this.tempForm.chuteNumber.join(','),
        excludeRuleId: this.temp.id
      }).then((res) => {
        if (res.data) {
          this.$message.warning(this.$t('CenterForSorting.chuteDuplicate'));
        }
      });
    },
    createData() {
      this.temp.chuteNumber = this.tempForm.chuteNumber.join(',');
      this.temp.isFallback = this.normalizeToBoolean(this.temp.isFallback);
      this.temp.isMachineFallbackRule = this.normalizeToBoolean(this.temp.isMachineFallbackRule);
      this.temp.conditions = this.buildSubmitConditions();
      this.$refs['dataForm'].validate((valid) => {
        if (valid) {
          this.dialogBtnLoading = true;
          addRule(this.temp)
            .then(() => {
              this.dialogFormVisible = false;
              this.dialogBtnLoading = false;
              this.$notify({
                title: this.$t('CenterForSorting.success'),
                message: this.$t('CenterForSorting.createSuccess'),
                type: 'success',
                duration: 2000
              });
              this.fetchRules();
            })
            .catch(() => {
              this.dialogBtnLoading = false;
            });
        }
      });
    },
    async handleEdit(row) {
      console.log(row, 'handleEdit');
      this.forceChangeEnabled = false;
      this.temp = JSON.parse(JSON.stringify(row)); // 深拷贝
      // 回显 parentRuleIds：转为 Number 匹配下拉的 :value="item.id"
      if (row.parentRuleId && row.parentRuleId !== '0') {
        const arr = row.parentRuleId.split(',').map(Number);
        this.$set(this.temp, 'parentRuleIds', arr);
      } else {
        this.$set(this.temp, 'parentRuleIds', []);
      }
      this.$set(this.temp, 'isFallback', this.normalizeToBoolean(this.temp.isFallback));
      this.$set(
        this.temp,
        'isMachineFallbackRule',
        this.normalizeToBoolean(this.temp.isMachineFallbackRule)
      );
      if (this.temp.isAbnormalRule === undefined || this.temp.isAbnormalRule === null) {
        this.$set(this.temp, 'isAbnormalRule', 0);
      } else {
        this.$set(this.temp, 'isAbnormalRule', Number(this.temp.isAbnormalRule));
      }
      this.$set(this.temp, 'oldIsFallback', this.temp.isFallback);
      this.$set(this.temp, 'oldIsMachineFallbackRule', this.temp.isMachineFallbackRule);
      // Ensure conditions is always an array to avoid RuleConditionForm render error
      if (!Array.isArray(this.temp.conditions)) {
        this.$set(this.temp, 'conditions', []);
      }

      const boundChutes = row.chuteNumber ? row.chuteNumber.split(',') : [];
      await Promise.all([this.generateChuteOptions(boundChutes), this.getListRules()]);

      // 使用 chuteNumber 作为目标格口值
      this.temp.chuteNumber = row.chuteNumber;
      this.tempForm.chuteNumber = boundChutes;

      listConditions({ ruleId: row.id }).then((res) => {
        const conditions = (res.data && res.data.conditions) || res.data || [];
        // 中文注释：兼容历史数据，若后端尚未回传新标记字段，则通过条件类型反推“分拣机兜底”状态。
        const hasMachineFallbackCondition = conditions.some(
          (item) => item && item.conditionType === 'MACHINE_FALLBACK'
        );
        if (hasMachineFallbackCondition && !this.isTrueFlag(this.temp.isMachineFallbackRule)) {
          this.$set(this.temp, 'isMachineFallbackRule', true);
          this.$set(this.temp, 'isFallback', false);
        }
        this.$set(this.temp, 'oldIsFallback', this.temp.isFallback);
        this.$set(this.temp, 'oldIsMachineFallbackRule', this.temp.isMachineFallbackRule);
        // 使用 $set 确保响应式，以防 temp 上不存在 'conditions' 属性
        this.$set(this.temp, 'conditions', conditions);
      });

      this.dialogStatus = 'update';
      this.dialogFormVisible = true;
      this.$nextTick(() => {
        this.$refs['dataForm'].clearValidate();
      });
    },
    async handleEditEnabled(row) {
      this.forceChangeEnabled = false;
      this.temp = JSON.parse(JSON.stringify(row)); // 深拷贝
      // 回显 parentRuleIds：转为 Number 匹配下拉的 :value="item.id"
      if (row.parentRuleId && row.parentRuleId !== '0') {
        const arr = row.parentRuleId.split(',').map(Number);
        this.$set(this.temp, 'parentRuleIds', arr);
      } else {
        this.$set(this.temp, 'parentRuleIds', []);
      }
      this.$set(this.temp, 'isFallback', this.normalizeToBoolean(this.temp.isFallback));
      this.$set(
        this.temp,
        'isMachineFallbackRule',
        this.normalizeToBoolean(this.temp.isMachineFallbackRule)
      );
      if (this.temp.isAbnormalRule === undefined || this.temp.isAbnormalRule === null) {
        this.$set(this.temp, 'isAbnormalRule', 0);
      } else {
        this.$set(this.temp, 'isAbnormalRule', Number(this.temp.isAbnormalRule));
      }
      this.$set(this.temp, 'oldIsFallback', this.temp.isFallback);
      this.$set(this.temp, 'oldIsMachineFallbackRule', this.temp.isMachineFallbackRule);

      const boundChutes = row.chuteNumber ? row.chuteNumber.split(',') : [];
      await Promise.all([this.generateChuteOptions(boundChutes, true), this.getListRules()]);

      this.temp.chuteNumber = row.chuteNumber;
      this.tempForm.chuteNumber = boundChutes;

      listConditions({ ruleId: row.id }).then((res) => {
        const conditions = (res.data && res.data.conditions) || res.data || [];
        const mappedConditions = conditions.map((c) => ({ ...c, isOriginal: true }));
        const hasMachineFallbackCondition = mappedConditions.some(
          (item) => item && item.conditionType === 'MACHINE_FALLBACK'
        );
        if (hasMachineFallbackCondition && !this.isTrueFlag(this.temp.isMachineFallbackRule)) {
          this.$set(this.temp, 'isMachineFallbackRule', true);
          this.$set(this.temp, 'isFallback', false);
        }
        this.$set(this.temp, 'oldIsFallback', this.temp.isFallback);
        this.$set(this.temp, 'oldIsMachineFallbackRule', this.temp.isMachineFallbackRule);
        this.$set(this.temp, 'conditions', mappedConditions);
      });

      this.dialogStatus = 'updateEnabled';
      this.dialogFormVisible = true;
      this.$nextTick(() => {
        this.$refs['dataForm'].clearValidate();
      });
    },
    updateData() {
      this.temp.chuteNumber = this.tempForm.chuteNumber.join(',');
      this.temp.isFallback = this.normalizeToBoolean(this.temp.isFallback);
      this.temp.isMachineFallbackRule = this.normalizeToBoolean(this.temp.isMachineFallbackRule);
      this.temp.conditions = this.buildSubmitConditions();
      this.$refs['dataForm'].validate((valid) => {
        if (valid) {
          this.dialogBtnLoading = true;
          updateRule(this.temp)
            .then(() => {
              this.dialogFormVisible = false;
              this.dialogBtnLoading = false;
              this.$notify({
                title: this.$t('CenterForSorting.success'),
                message: this.$t('CenterForSorting.updateSuccess'),
                type: 'success',
                duration: 2000
              });
              this.fetchRules();
            })
            .catch(() => {
              this.dialogBtnLoading = false;
            });
        }
      });
    },
    handleDelete(row) {
      this.$confirm(
        this.$t('CenterForSorting.AreYouSureDelete'),
        this.$t('CenterForSorting.tips'),
        {
          confirmButtonText: this.$t('CenterForSorting.sure'),
          cancelButtonText: this.$t('CenterForSorting.cancel'),
          type: 'warning'
        }
      ).then(() => {
        deleteRule({ id: row.id }).then(() => {
          this.$message({
            type: 'success',
            message: this.$t('CenterForSorting.DeleteSucceeded')
          });
          this.fetchRules();
        });
      });
    },
    handleViewConditions(row) {
      this.currentRuleId = row.id;
      this.viewConditionsVisible = true;
    },
    openRuleGroupChangeLog() {
      if (!this.ruleGroupId) {
        this.$message.warning(this.$t('CenterForSorting.selectRuleGroup'));
        return;
      }
      this.changeLogDialogType = 'group';
      this.changeLogRuleId = null;
      this.openRuleChangeLogDialog();
    },
    openRuleChangeLog(row) {
      this.changeLogDialogType = 'rule';
      this.changeLogRuleId = row.id;
      this.openRuleChangeLogDialog();
    },
    openRuleChangeLogDialog() {
      this.changeLogDialogVisible = true;
      this.resetRuleChangeLogState();
      this.fetchRuleChangeLogs();
    },
    handleCloseRuleChangeLog() {
      this.changeLogDialogVisible = false;
      this.resetRuleChangeLogState();
    },
    resetRuleChangeLogState() {
      this.ruleChangeLogList = [];
      this.ruleChangeLogPageNumber = 1;
      this.ruleChangeLogTotal = 0;
      this.ruleChangeLogFinished = false;
    },
    async fetchRuleChangeLogs() {
      if (this.ruleChangeLogLoading || this.ruleChangeLogFinished) return;
      this.ruleChangeLogLoading = true;

      const params = {};
      if (this.changeLogDialogType === 'group') {
        params.ruleGroupId = this.ruleGroupId;
      } else {
        params.ruleId = this.changeLogRuleId;
      }

      try {
        const res = await getRuleChangeLogPage({
          pageNumber: this.ruleChangeLogPageNumber,
          pageSize: this.ruleChangeLogPageSize,
          params
        });
        const records = (res.data && res.data.records) || [];
        const total = (res.data && res.data.total) || 0;
        this.ruleChangeLogList = this.ruleChangeLogList.concat(records);
        this.ruleChangeLogTotal = total;
        this.ruleChangeLogPageNumber += 1;
        if (
          this.ruleChangeLogList.length >= this.ruleChangeLogTotal ||
          records.length < this.ruleChangeLogPageSize
        ) {
          this.ruleChangeLogFinished = true;
        }
      } finally {
        this.ruleChangeLogLoading = false;
      }
    },
    // 中文注释：滚动到底自动加载下一页（每页10条），用于规则变更日志懒加载。
    handleRuleChangeLogScroll() {
      const scrollWrap = this.$refs.ruleChangeLogScrollWrap;
      if (!scrollWrap || this.ruleChangeLogLoading || this.ruleChangeLogFinished) return;
      const reachedBottom =
        scrollWrap.scrollTop + scrollWrap.clientHeight >= scrollWrap.scrollHeight - 24;
      if (reachedBottom) {
        this.fetchRuleChangeLogs();
      }
    },
    getOperationTypeLabel(operationType) {
      const key = `CenterForSorting.${operationType}`;
      const translated = this.$t(key);
      return translated === key ? operationType : translated;
    },
    openChangeCompare(logItem) {
      this.currentBeforeData = logItem.beforeData;
      this.currentAfterData = logItem.afterData;
      this.changeCompareVisible = true;
    },
    startAnimation() {
      this.activeStep = 0;
      if (this.animationTimer) clearInterval(this.animationTimer);

      this.animationTimer = setInterval(() => {
        if (this.activeStep < 6) {
          this.activeStep++;
        } else {
          this.activeStep = 0;
        }
      }, 800);
    },
    stopAnimation() {
      if (this.animationTimer) {
        clearInterval(this.animationTimer);
        this.animationTimer = null;
      }
      this.activeStep = 0;
    },
    // 复制规则相关方法
    async handleCopyOpen() {
      if (!(await this.checkOperatePermissionBeforeAction())) return;
      this.copySourceMachineId = null;
      this.copySourceRuleGroupId = null;
      this.copySourceRuleGroups = [];
      this.copyRuleList = [];
      this.copySelectedRules = [];

      // 生成当前规则组的可用格口选项
      await this.generateChuteOptions();

      this.copyDialogVisible = true;
      if (this.$refs.copyRulesTable) {
        this.$refs.copyRulesTable.clearSelection();
      }
    },
    // 导入规则
    async handleImportOpen() {
      console.log(this.ruleGroupId, 'this.ruleGroupId');
      if (!(await this.checkOperatePermissionBeforeAction())) return;
      this.$refs.bulkImportRef.handleOpen(this.ruleGroupId);
    },
    // 新增方法：处理导入成功后的回调
    handleImportSuccess() {
      // 刷新规则列表
      this.fetchRules();
      // this.$message.success(this.$t('CenterForSorting.operateSuccess'));
    },
    closeCopyDialog() {
      this.copyDialogVisible = false;
    },
    async handleCopyMachineChange() {
      this.copySourceRuleGroupId = null;
      this.copySourceRuleGroups = [];
      this.copyRuleList = [];
      this.copySelectedRules = [];
      if (this.$refs.copyRulesTable) {
        this.$refs.copyRulesTable.clearSelection();
      }
      if (!this.copySourceMachineId) return;

      const params = {
        pageNumber: 1,
        pageSize: 1000,
        params: { machineId: this.copySourceMachineId }
      };
      const res = await getRuleGroupPage(params);
      this.copySourceRuleGroups = res.data.records || [];
    },
    async fetchCopySourceRules() {
      if (!this.copySourceRuleGroupId) {
        this.copyRuleList = [];
        this.copySelectedRules = [];
        if (this.$refs.copyRulesTable) {
          this.$refs.copyRulesTable.clearSelection();
        }
        return;
      }
      this.copyListLoading = true;
      try {
        const response = await getListRules({
          ruleGroupId: this.copySourceRuleGroupId,
          conditionList: this.conditionList
        });
        const rules = response.data || [];

        // 初始化每个规则的选中格口，默认为原规则的格口（如果当前可用）
        this.copyRuleList = rules.map((rule) => {
          const originalChutes = rule.chuteNumber ? rule.chuteNumber.split(',') : [];
          // 过滤掉在当前机器中不可用的格口
          const validChutes = originalChutes.filter((chute) => {
            const option = this.targetChuteOptions.find(
              (opt) => String(opt.value) === String(chute)
            );
            return option && !option.disabled;
          });

          return {
            ...rule,
            newChuteNumberArr: validChutes
          };
        });
        this.copySelectedRules = [];
        if (this.$refs.copyRulesTable) {
          this.$refs.copyRulesTable.clearSelection();
        }
      } catch (error) {
        console.error('Failed to fetch source rules:', error);
      } finally {
        this.copyListLoading = false;
      }
    },
    handleCopySelectionChange(selection) {
      this.copySelectedRules = selection;
    },
    async confirmCopyRules() {
      if (this.copySelectedRules.length === 0) {
        this.$message.warning(this.$t('CenterForSorting.pleaseSelectRules'));
        return;
      }

      // 验证是否都分配了目标格口
      const rulesToCopy = [...this.copySelectedRules];
      for (const rule of rulesToCopy) {
        if (!rule.newChuteNumberArr || rule.newChuteNumberArr.length === 0) {
          this.$message.warning(
            `${rule.ruleName}: ${this.$t('CenterForSorting.pleaseInput')}${this.$t(
              'CenterForSorting.targetChute'
            )}`
          );
          return;
        }
      }

      this.copyBtnLoading = true;
      let successCount = 0;

      try {
        // 获取当前最大的优先级
        const priorityRes = await getNextPriority({ ruleGroupId: this.ruleGroupId });
        let nextPriority = priorityRes.data || 1;

        // 依次复制选中的规则
        for (const rule of rulesToCopy) {
          // 获取原规则的条件
          const conditionRes = await listConditions({ ruleId: rule.id });
          const conditions =
            (conditionRes.data && conditionRes.data.conditions) || conditionRes.data || [];

          const newRule = {
            ruleGroupId: this.ruleGroupId,
            ruleName: rule.ruleName,
            chuteNumber: rule.newChuteNumberArr.join(','),
            priority: nextPriority,
            matchLogic: rule.matchLogic,
            isFallback: rule.isFallback || false,
            isMachineFallbackRule: rule.isMachineFallbackRule || false,
            description: rule.description,
            conditions: conditions
          };

          await addRule(newRule);

          // 成功后清除该行的勾选状态
          if (this.$refs.copyRulesTable) {
            this.$refs.copyRulesTable.toggleRowSelection(rule, false);
          }

          nextPriority++;
          successCount++;
        }

        this.$notify({
          title: this.$t('CenterForSorting.success'),
          message: this.$t('CenterForSorting.copySuccess'),
          type: 'success',
          duration: 2000
        });

        this.copyDialogVisible = false;
        this.fetchRules();
      } catch (error) {
        console.error('Copy rules error:', error);
        if (successCount > 0) {
          this.fetchRules(); // 刷新已成功复制的规则
        }
        this.$notify({
          title: this.$t('CenterForSorting.tips'),
          message: this.$t('CenterForSorting.copyFailed'),
          type: 'error',
          duration: 3000
        });
      } finally {
        this.copyBtnLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.app-container {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 84px);
}

.table-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.table-card ::v-deep .el-card__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 10px;
}

.filter-container {
  padding-bottom: 10px;
}

.sortable-ghost {
  opacity: 0.8;
  color: #fff !important;
  background: #42b983 !important;
}

.rule-match-animation {
  padding: 15px;
  background: #f4f4f5;
  border-radius: 4px;
}

.animation-track {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}

.step-node {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: 0.3;
  transition: opacity 0.3s;
}

.step-node.active {
  opacity: 1;
}

.node-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #909399;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-bottom: 5px;
}

.step-node.active .node-icon {
  background: #00a551;
}

.step-node.rejected .node-icon {
  background: #f56c6c;
}

.step-node.matched .node-icon {
  background: #67c23a;
}

.node-label {
  font-size: 12px;
  color: #606266;
}

.node-status {
  position: absolute;
  top: -5px;
  right: -5px;
  background: #fff;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.step-arrow {
  margin: 0 15px;
  color: #c0c4cc;
  font-size: 20px;
  opacity: 0.3;
  transition: all 0.3s;
}

.step-arrow.active {
  opacity: 1;
  color: #00a551;
  transform: translateX(5px);
}

.step-result {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #67c23a;
  opacity: 0.3;
  transition: opacity 0.3s;
}

.step-result.active {
  opacity: 1;
  transform: scale(1.1);
}

.step-result i {
  font-size: 32px;
  margin-bottom: 5px;
}

.result-label {
  font-size: 12px;
  font-weight: bold;
}

.skipped {
  opacity: 0.1 !important;
}

.animation-desc {
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
  padding-top: 10px;
  border-top: 1px dashed #dcdfe6;
}

.rule-name-cell {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
}

.rule-name-tag {
  margin-right: 4px;
  transform: scale(0.9);
  transform-origin: left center;
  flex-shrink: 0;
}

.rule-name-tag-fallback {
  color: #e6a23c;
  border-color: #f3d19e;
  background-color: #fdf6ec;
}

.rule-name-tag-machine-fallback {
  color: #00a551;
  border-color: #cfead9;
  background-color: #f2fbf6;
}

.rule-name-text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rule-name-icon {
  margin-left: 6px;
  flex-shrink: 0;
  cursor: pointer;
  color: #00a551;
  font-size: 16px;
}

.fallback-check-group {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.fallback-check-item {
  display: inline-flex;
  align-items: center;
}

.fallback-check-tip {
  margin-left: 4px;
  color: #909399;
  cursor: pointer;
}

.rule-dialog-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: 36px;
}

.rule-action-item {
  display: inline-block;
  margin-right: 8px;
}

.rule-more-action-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: #606266;
  cursor: pointer;
  border-radius: 4px;
}

.rule-more-action-trigger:hover {
  background: #f5f7fa;
  color: #00a551;
}

.dropdown-item-text {
  display: inline-block;
  max-width: 110px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.rule-match-reference {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.rule-match-reference-text {
  max-width: 120px;
  font-size: 14px;
  color: #606266;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rule-explain-tooltip {
  width: 460px;
  font-size: 13px;
  line-height: 1.6;
  color: #ffffff;
  text-align: left;
  word-break: break-word;
}

.rule-explain-tooltip > div {
  margin: 0;
}

.rule-change-log-wrap {
  max-height: 460px;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 10px 14px;
  background: #fafcff;
}

.rule-change-log-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 4px;
  border-bottom: 1px dashed #ebeef5;
}

.rule-change-log-item:last-child {
  border-bottom: none;
}

.rule-change-log-content {
  min-width: 0;
  padding-right: 10px;
}

.rule-change-log-main {
  color: #303133;
  line-height: 20px;
  word-break: break-all;
}

.rule-change-log-sub {
  margin-top: 4px;
  color: #909399;
  font-size: 12px;
}

.rule-change-detail-icon {
  color: #00a551;
  font-size: 16px;
  cursor: pointer;
  flex-shrink: 0;
}

.rule-change-log-tip {
  padding: 12px 0 6px;
  text-align: center;
  color: #909399;
  font-size: 13px;
}

.network-point-refresh-tip {
  margin-top: 8px;
  padding-left: 190px;
  font-size: 12px;
  line-height: 20px;
  color: #909399;
}

.network-point-refresh-link {
  color: #00a551;
  text-decoration: underline;
  cursor: pointer;
  margin: 0 2px;
}

.network-point-refresh-icon {
  margin-right: 4px;
  color: #e6a23c;
  font-size: 14px;
  vertical-align: middle;
}

.network-point-refresh-link:hover {
  color: #008f45;
}

.network-point-refresh-link.is-loading {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
